import { PurchaseRequestFlowStepper } from "@fero/components";
import { PortalPageContent, PortalPageContentHeader } from "@fero/layouts";
import { useTradeSalesStore } from "@fero/stores";
import { AppRoutes } from "@fero/utils";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import LoadingButton from "@mui/lab/LoadingButton";
import { Alert, Button, Grid, Stack, Typography } from "@mui/material";
import type { WithDbId } from "adl-gen/common/db";
import type {
  NominatedPurchaserDetailsView,
  ProductSalePriceInfo,
} from "adl-gen/ferovinum/app/api";
import type { StorageLocation } from "adl-gen/ferovinum/app/db";
import { useConfirmationDialog } from "components/context/global-dialog/use-dialog";
import { PurchaseOrderUploadCard } from "components/library/widgets/wizard-card/purchase-order-upload-card";
import { Loader } from "components/widgets/loader/loader";
import { type FormikProps, useFormik } from "formik";
import { useCallback, useMemo } from "react";
import { isLoaded, type LoadingValue } from "utils/utility-types";
import type {
  PurchaseRequestFlowState,
  StorageListing,
} from "../purchase-request-types";
import {
  DeliveryTypeCard,
  LocationSelectorCard,
  ProductSelectionCard,
  PurchaserSelectorCard,
  SalePriceTypeSelectorCard,
} from "./components";
import { getInitialValuesForProductsForm } from "./components/initial-form-values";
import { validationSchema } from "./components/schemas";
import type { SetupPurchaserRequestFormValues } from "./organisation-purchase-request-setup-page";

export interface OrganisationPurchaseRequestSetupPageViewProps {
  onPurchaserSelected: (
    purchaser: NominatedPurchaserDetailsView | null,
  ) => void;
  storageLocations: WithDbId<StorageLocation>[];
  activeStorageLocation?: WithDbId<StorageLocation>;
  onChangeStorageLocation: (loc: WithDbId<StorageLocation> | null) => void;
  onNext: (values: SetupPurchaserRequestFormValues) => Promise<void>;
  initialValues?: PurchaseRequestFlowState["setup"];
  showProductionOrders?: boolean;
  finishedProducts: LoadingValue<StorageListing[]>;
  finishableProducts: LoadingValue<StorageListing[]>;
  productsDefaultSalePrices: LoadingValue<ProductSalePriceInfo[]>;
}

export const OrganisationPurchaseRequestSetupPageView = (
  props: OrganisationPurchaseRequestSetupPageViewProps,
) => {
  const form = useFormik<SetupPurchaserRequestFormValues>({
    initialValues: getInitialValuesForProductsForm(props.initialValues),
    validationSchema: validationSchema,
    validateOnMount: false,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: props.onNext,
    enableReinitialize: true,
  });

  const onSelectedPurchaserChange = useCallback(
    (purchaser: NominatedPurchaserDetailsView | null): void => {
      props.onPurchaserSelected(purchaser);
      form.setFieldValue("purchaser", purchaser);
    },
    [form, props],
  );

  return (
    <PortalPageContent
      header={
        <OrganisationPurchaseRequestSetupHeader
          purchaserName={form.values.purchaser?.name ?? "Purchaser"}
        />
      }
    >
      <Grid container spacing={5}>
        <Grid item xs={12}>
          <PurchaserSelectorCard
            selectedPurchaser={form.values.purchaser}
            onChange={onSelectedPurchaserChange}
          />
        </Grid>
        {form.values.purchaser && (
          <SetupFormSection
            form={form}
            selectedPurchaser={form.values.purchaser}
            {...props}
          />
        )}
      </Grid>
    </PortalPageContent>
  );
};

const OrganisationPurchaseRequestSetupHeader = ({
  purchaserName,
}: {
  purchaserName: string;
}) => {
  const { options } = useTradeSalesStore();
  return (
    <PortalPageContentHeader
      variant="split"
      title={
        <Stack>
          <Typography variant={"h4"}>
            {options.isEditMode ? "Edit Trade Sale" : "New Trade Sale"}
          </Typography>
          <Typography>from {purchaserName}</Typography>
        </Stack>
      }
      right={<PurchaseRequestFlowStepper activeStep={0} />}
    />
  );
};

const SetupFormSection = ({
  form,
  selectedPurchaser,
  storageLocations,
  activeStorageLocation,
  onChangeStorageLocation,
  showProductionOrders,
  finishedProducts,
  finishableProducts,
  productsDefaultSalePrices,
}: OrganisationPurchaseRequestSetupPageViewProps & {
  selectedPurchaser: NominatedPurchaserDetailsView;
  form: FormikProps<SetupPurchaserRequestFormValues>;
}) => {
  const isGbLocation = activeStorageLocation?.value.country === "GB";

  const { showConfirmationDialog } = useConfirmationDialog();
  const onChangeLocation = async (loc: WithDbId<StorageLocation> | null) => {
    const { products } = form.touched;
    const resetProducts = () => {
      form.setFieldValue("products", []);
      form.setFieldValue("salePriceType", "exDutyAndVat");
      form.setFieldValue("bondedSale", true);
    };
    if ((products?.length || 0) === 0) {
      resetProducts();
      return onChangeStorageLocation(loc);
    }

    const userResponse = await showConfirmationDialog({
      title: "Are you sure you want to change the storage location?",
      body: "You will lose your current product selections and price/quantity details.",
    });
    if (userResponse.kind === "ok") {
      resetProducts();
      onChangeStorageLocation(loc);
    }
  };

  const onChangeDeliveryType = async (isBonded: boolean) => {
    form.setFieldValue("bondedSale", isBonded);
    if (isGbLocation) {
      form.setFieldValue(
        "salePriceType",
        isBonded ? "exDutyAndVat" : "incDutyExVat",
      );
    } else {
      form.setFieldValue(
        "salePriceType",
        isBonded ? "exDutyAndVat" : "incDutyAndVat",
      );
    }
  };

  const showPoUpload = useMemo(
    () => form.values.purchaser?.poUploadEnabled === true,
    [form.values.purchaser?.poUploadEnabled],
  );

  return storageLocations.length > 0 ? (
    <>
      {showPoUpload && (
        <Grid item xs={12}>
          <PurchaseOrderUploadCard
            file={form.values.poFile}
            onFileSelected={(file) => form.setFieldValue("poFile", file)}
            onFileRemoved={() => form.setFieldValue("poFile", undefined)}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        <LocationSelectorCard
          selectedLocation={activeStorageLocation}
          storageLocations={storageLocations}
          onChange={onChangeLocation}
        />
      </Grid>
      <Grid item xs={12}>
        <DeliveryTypeCard
          isBonded={form.values.bondedSale}
          onChange={onChangeDeliveryType}
        />
        {!form.values.bondedSale && isGbLocation && (
          <Grid item xs={12}>
            <SalePriceTypeSelectorCard
              salePrice={form.values.salePriceType}
              onChange={(value) => form.setFieldValue("salePriceType", value)}
            />
          </Grid>
        )}
      </Grid>

      <Grid item xs={12}>
        <Loader
          loadingStates={[
            finishableProducts,
            finishedProducts,
            productsDefaultSalePrices,
          ]}
        >
          {isLoaded(finishableProducts) &&
            isLoaded(finishedProducts) &&
            isLoaded(productsDefaultSalePrices) && (
              <>
                {!form.values.bondedSale && !isGbLocation && (
                  <Alert severity="info">
                    Please ensure the sale price entered includes all fees such
                    as Duty and VAT
                  </Alert>
                )}
                <ProductSelectionCard
                  selectedPurchaser={selectedPurchaser}
                  finishableProductListings={finishableProducts.value}
                  finishedProductListings={finishedProducts.value}
                  form={form}
                  productsDefaultSalePrices={productsDefaultSalePrices.value}
                  activeStorageLocation={activeStorageLocation}
                  showProductionOrders={showProductionOrders}
                />
              </>
            )}
        </Loader>
      </Grid>
      <Grid item xs={12}>
        <Stack spacing={2} direction="row" justifyContent={"flex-end"}>
          <Button
            variant="outlined"
            href={AppRoutes.OrganisationPurchaseRequests}
          >
            Back
          </Button>
          <LoadingButton
            variant="contained"
            onClick={form.submitForm}
            disabled={form.values.products.length <= 0}
          >
            Next
          </LoadingButton>
        </Stack>
      </Grid>
    </>
  ) : (
    <Grid item xs={12}>
      <Loader loadingStates={[finishedProducts, finishableProducts]}>
        <Stack direction="row" spacing={1}>
          <WarningAmberOutlinedIcon color="warning" fontSize="small" />
          <Typography>
            No products are available in any storage location for a trade sale
          </Typography>
        </Stack>
      </Loader>
    </Grid>
  );
};
